import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Badge,
  Button,
  DataTable,
  TableCellEllipsis,
  clientSortRows,
} from "./ui";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../services";
import { invalidateSetupGuideCache } from "../hooks/useSetupGuideStatus";
import { partnerConnectReturnPath, readPartnerSession, writePartnerSession } from "../utils/partnerSession";
import { cancelPartnerSession, markPartnerBranchLive } from "./PartnerSessionBanner";
import CreateStoreModal from "./Modals/CreateStoreModal";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ChevronDown, X, Copy, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getTabSenseRedirectUri,
  getTabSenseAuthorizeUrl,
  getTabSenseClientId,
} from "../utils/tabSenseOAuth";
import { subscriptionConnectErrorMessage, validationMessageFromApiData } from "../utils/formatApiValidationError";
import { handlePlatformLogoError, resolveIntegrationLogo } from "../utils/platformLogos";
import {
  branchPosConflictTooltipKey,
  omitOppositePosShopIdsFromBranchPayload,
} from "../utils/posReadOnly";
import SubscriptionConnectBanner from "./subscription/SubscriptionConnectBanner";
import "./subscription/SubscriptionFlow.css";

function isNinjaAvailableApp(app) {
  return /ninja/i.test(`${app?.name || ""} ${app?.appType || ""} ${app?.application || ""}`);
}

function refId(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") return String(value._id || value.id || value.$oid || "");
  return String(value);
}

function storeTitle(store) {
  if (!store) return "";
  if (typeof store.displayName === "string") return store.displayName;
  if (typeof store.name === "string") return store.name;
  return store.name?.en || store.name?.ar || "";
}

function AddApp() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  
  // Map exact backend `message` / `error` strings to ar/en via i18n keys in locales
  const translateError = (backendMessage) => {
    if (backendMessage == null) return "";
    const s = String(backendMessage).trim();
    if (!s) return "";
    return t(s, { defaultValue: s });
  };

  const branchUpdateErrorMessage = (err) => {
    const d = err?.response?.data;
    const limitMessage = subscriptionConnectErrorMessage(d, t);
    if (limitMessage) return limitMessage;
    const combined = validationMessageFromApiData(d, translateError);
    if (combined && !/^server error\b/i.test(combined)) return combined;
    if (d && typeof d === "object" && typeof d.error === "string" && d.error.trim()) {
      return translateError(d.error);
    }
    return "";
  };

  const wrapPosConflictTooltip = (node, tooltipKey) => {
    if (!tooltipKey) return node;
    const tooltipText = t(tooltipKey);
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip dir={i18n.dir()}>{tooltipText}</Tooltip>}
      >
        <span
          className="d-block w-100"
          style={{ cursor: "not-allowed" }}
          tabIndex={0}
          aria-label={tooltipText}
        >
          <span style={{ pointerEvents: "none", display: "block" }}>{node}</span>
        </span>
      </OverlayTrigger>
    );
  };

  // State to track Foodics app auth status
  const [isFoodicsAuthed, setIsFoodicsAuthed] = useState(false);
  // State for create store modal
  const [showCreateStoreModal, setShowCreateStoreModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const appId = params.id;
  const partnerReturnTo = searchParams.get("returnTo")
    || (searchParams.get("partnerSession")
      ? partnerConnectReturnPath(searchParams.get("partnerSession"))
      : "");
  const partnerBranchId = searchParams.get("branchId") || "";

  const returnAfterPartnerConnect = (branchId) => {
    markPartnerBranchLive(branchId);
    const session = readPartnerSession();
    if (session?.id) {
      writePartnerSession({
        ...session,
        linkStatus: "live",
        branchId: branchId || session.branchId || null,
        step: "connect",
      });
    }
    if (partnerReturnTo) {
      setTimeout(() => navigate(partnerReturnTo), 800);
      return true;
    }
    if (session?.id) {
      setTimeout(() => navigate(`/partner-sessions/${session.id}?step=connect`), 800);
      return true;
    }
    return false;
  };
  const [copiedId, setCopiedId] = useState(null);
  const [togglingBranch, setTogglingBranch] = useState(null);
  const [connectInfo, setConnectInfo] = useState(null);
  const [connectRefresh, setConnectRefresh] = useState(0);
  const [editingCreds, setEditingCreds] = useState(false);

  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [appDetails, setAppDetails] = useState(null);
  const [appData, setAppData] = useState({
    clientId: "",
    clientSecret: "",
    branchId: "",
    integrationId: "",
  });

  const [integratedAppIds, setIntegratedAppIds] = useState([]);
  const [apps, setApps] = useState([]);
  const [keetaShops, setKeetaShops] = useState([]);
  const [foodicsOutlets, setFoodicsOutlets] = useState([]);
  const [tabSenseShops, setTabSenseShops] = useState([]);
  const [chefzShops, setChefzShops] = useState([]);
  const fetchApps = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await api.get("/apps", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allApps = res.data.apps || [];
      setApps(allApps);

      const integratedIds = allApps.map((app) => app.availableAppId);
      setIntegratedAppIds(integratedIds);

      // Auto-fill credentials based on selected store and app match
      if (selectedStore) {
        updateCredentialsForSelectedStore(allApps);
      }
    } catch (err) {
      console.error("Failed to load apps:", err.message);
      setError(`${t("Failed to load apps.")}`);
    }
  };
  const [storeHasKeetaIntegration, setStoreHasKeetaIntegration] =
    useState(false);
  const [storeHasFoodicsIntegration, setStoreHasFoodicsIntegration] =
    useState(false);
  const [storeHasChefzIntegration, setStoreHasChefzIntegration] =
    useState(false);
  const [storeHasTabSenseIntegration, setStoreHasTabSenseIntegration] =
    useState(false);
  console.log("keeta present", storeHasKeetaIntegration);

  const checkKeetaIntegration = async () => {
    if (!selectedStore) return;

    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${selectedStore}/apps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("api response here", response);

      const hasKeeta = response.data.apps?.some(
        (app) => app.application?.toLowerCase() === "keeta"
      );
      console.log("is integrated", hasKeeta);

      setStoreHasKeetaIntegration(!!hasKeeta);
    } catch (err) {
      console.error("Failed to check Keeta integration:", err.message);
      setStoreHasKeetaIntegration(false);
    }
  };

  const checkFoodicsIntegration = async () => {
    if (!selectedStore) return;
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${selectedStore}/apps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const hasFoodics = response.data.apps?.some(
        (app) => app.application?.toLowerCase() === "foodics"
      );
      console.log("is there foodics", hasFoodics);
      setStoreHasFoodicsIntegration(!!hasFoodics);
    } catch (err) {
      setStoreHasFoodicsIntegration(false);
    }
  };

  const checkChefzIntegration = async () => {
    if (!selectedStore) return;
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${selectedStore}/apps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const hasChefz = response.data.apps?.some(
        (app) => app.application?.toLowerCase().includes("chefz")
      );
      setStoreHasChefzIntegration(!!hasChefz);
    } catch (err) {
      console.error("Error checking Chefz integration:", err);
      setStoreHasChefzIntegration(false);
    }
  };

  const checkTabSenseIntegration = async () => {
    if (!selectedStore) return;
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${selectedStore}/apps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const hasTabSense = response.data.apps?.some(
        (app) => app.application?.toLowerCase() === "tabsense"
      );
      setStoreHasTabSenseIntegration(!!hasTabSense);
    } catch (err) {
      console.error("Failed to check TabSense integration:", err.message);
      setStoreHasTabSenseIntegration(false);
    }
  };

  const fetchAllKeetaShops = async (storeId) => {
    if (!storeId) return; // Don't make the call if no store is selected

    try {
      const response = await api.get(`/keeta-shops/store/${storeId}/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      console.log("fetch all keeta shops", response);

      const shops = response.data.data?.shops || [];
      setKeetaShops(shops);
    } catch (error) {
      console.error("Error:", error);
      setKeetaShops([]);
    }
  };

  const fetchFoodicsOutlets = async (storeId) => {
    console.log(storeId);
    if (!storeId) return;
    try {
      const response = await api.get(`/foodics-shops/store/${storeId}/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      console.log("fetch all foodics is ", response);
      const shops = response.data.data?.shops || [];
      console.log("shops are", shops);
      setFoodicsOutlets(shops);
      console.log(foodicsOutlets);
    } catch (error) {
      setFoodicsOutlets([]);
    }
  };

  const fetchTabSenseShops = async (storeId) => {
    if (!storeId) return;
    try {
      const response = await api.get(`/tabsense-shops/store/${storeId}/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const shops = response.data.data?.shops || [];
      setTabSenseShops(shops);
    } catch (error) {
      setTabSenseShops([]);
    }
  };

  const fetchChefzShops = async (storeId) => {
    if (!storeId) return;
    try {
      const response = await api.get(`/chefz-shops/store/${storeId}/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      console.log("fetch all chefz shops", response);
      const shops = response.data.data?.shops || [];
      setChefzShops(shops);
    } catch (error) {
      console.error("Error fetching Chefz shops:", error);
      setChefzShops([]);
    }
  };

  // Update the useEffect
  useEffect(() => {
    if (selectedStore) {
      fetchAllKeetaShops(selectedStore);
      fetchFoodicsOutlets(selectedStore);
      fetchTabSenseShops(selectedStore);
      fetchChefzShops(selectedStore);
    }
  }, [selectedStore]);

  const updateCredentialsForSelectedStore = (allApps = apps) => {
    if (!selectedStore || !appId) {
      // Clear credentials if no store is selected
      if (appDetails?.credentialsTemplate) {
        const clearedData = {};
        Object.keys(appDetails.credentialsTemplate).forEach((key) => {
          clearedData[key] = "";
        });
        setAppData((prev) => ({ ...prev, ...clearedData }));
      }
      return;
    }

    // Find matching app based on availableAppId and store._id
    const matchedApp = allApps.find((app) => (
      String(app.availableAppId?._id || app.availableAppId) === String(appId)
      && String(app.store?._id || app.store) === String(selectedStore)
    ));

    if (matchedApp && matchedApp.userCredentials) {
      setAppData((prev) => ({
        ...prev,
        ...matchedApp.userCredentials,
      }));
    } else {
      // Clear credentials if no match found
      if (appDetails?.credentialsTemplate) {
        const clearedData = {};
        Object.keys(appDetails.credentialsTemplate).forEach((key) => {
          clearedData[key] = "";
        });
        setAppData((prev) => ({ ...prev, ...clearedData }));
      }
    }
  };

  const isFoodicsCheck = () => {
    if (!appDetails) return false;
    return (
      appDetails.appType === "FOODICS" ||
      appDetails.name?.toLowerCase() === "foodics" ||
      appDetails.application?.toLowerCase() === "foodics"
    );
  };

  const handleStoreSelect = (store) => {
    if (store?._id === "__CREATE_STORE__") {
      setSelectedStore("");
      setStoreError("");
      setBranches([]);
      setShowCreateStoreModal(true);
      return;
    }
    if (store?._id) {
      setSelectedStore(store._id);
      setStoreError(""); // Clear store error when a store is selected
    } else {
      setSelectedStore("");
      setBranches([]); // Clear branches when no store is selected
      if (!isFoodicsCheck() || (isFoodicsApp && isFoodicsAuthed)) {
        setStoreError(t("Store is required"));
      }
    }
  };

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [branchTableSortColumn, setBranchTableSortColumn] = useState("name");
  const [branchSortDirection, setBranchSortDirection] = useState("asc");
  const [branchCurrentPage, setBranchCurrentPage] = useState(1);
  const [branchPageSize, setBranchPageSize] = useState(10);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedStoreId, setCopiedStoreId] = useState(null);
  const storeIdFromState = location.state?.storeId;
  const didAutoSelectStore = useRef(false);
  const { errorMessage } = location.state || {};
  const [fieldErrors, setFieldErrors] = useState({
    store: "",
    branch: "",
  });
  
  // Ref to prevent duplicate error toasts
  const lastErrorShown = useRef(null);

  useEffect(() => {
    if (errorMessage && errorMessage !== lastErrorShown.current) {
      lastErrorShown.current = errorMessage;
      toast.error(translateError(errorMessage), { autoClose: 2000 });

      // Clear only errorMessage, keep rest of state
      const { errorMessage: _, ...rest } = location.state || {};
      navigate(location.pathname, { replace: true, state: rest });
    }
    
    // Reset ref when errorMessage is cleared
    if (!errorMessage && lastErrorShown.current) {
      lastErrorShown.current = null;
    }
  }, [errorMessage, navigate, location.pathname]);

  useEffect(() => {
    if (!appId) {
      navigate("/apps-management");
    } else {
      fetchUserStores();
      fetchAppDetails();
      fetchApps();
    }
  }, [appId, navigate]);

  useEffect(() => {
    if (stores && stores.length > 0) {
      const updated = stores.map((store) => {
        const localizedName = isArabic
          ? store?.name?.ar || store?.name_ar || store?.name?.en || store?.name
          : store?.name?.en || store?.name;
        return { ...store, displayName: localizedName || store?.displayName };
      });
      setStores(updated);
    }
    if (branches && branches.length > 0) {
      const updatedB = branches.map((branch) => {
        const localizedName = isArabic
          ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
          : branch?.name?.en || branch?.name;
        return { ...branch, displayName: localizedName || branch?.displayName };
      });
      setBranches(updatedB);
    }
  }, [isArabic]);

  useEffect(() => {
    if (didAutoSelectStore.current || selectedStore || !stores.length) return;
    const fromRoute = storeIdFromState
      ? stores.find((store) => String(store._id) === String(storeIdFromState))
      : null;
    if (fromRoute) {
      didAutoSelectStore.current = true;
      setSelectedStore(fromRoute._id);
      return;
    }
    if (!apps.length || !appId || !appDetails) return;
    const connected = stores.find((store) => apps.some((app) => {
      if (refId(app.store?._id || app.store) !== refId(store._id)) return false;
      if (String(app.status || "").toLowerCase() === "disconnected") return false;
      if (refId(app.availableAppId) === refId(appId)) return true;
      const pageIsJahez = /jahez/i.test(
        `${appDetails?.name || ""} ${appDetails?.appType || ""} ${appDetails?.application || ""}`
      );
      return pageIsJahez && /jahez/i.test(`${app.application || ""} ${app.availableAppId?.name || ""}`);
    }));
    didAutoSelectStore.current = true;
    if (connected?._id) setSelectedStore(connected._id);
  }, [stores, apps, appId, appDetails, selectedStore, storeIdFromState]);

  useEffect(() => {
    if (selectedStore) {
      fetchBranches();
      checkKeetaIntegration();
      checkFoodicsIntegration();
      checkChefzIntegration();
      checkTabSenseIntegration();
    }
    // Update credentials when store selection changes
    updateCredentialsForSelectedStore();
  }, [selectedStore, apps, appDetails]);

  const [storeError, setStoreError] = useState("");

  const validateFields = () => {
    const errors = {};
    let isValid = true;

    // Reset all errors
    setStoreError("");
    setFieldErrors({});

    // Validate store field
    if (
      !selectedStore &&
      (!isFoodicsCheck() || (isFoodicsApp && isFoodicsAuthed))
    ) {
      setStoreError(t("Store is required"));
      isValid = false;
    }

    // Skip credential validation for OAuth apps (Keeta, Foodics)
    // These apps handle credentials through OAuth flow, not manual entry
    const appName = appDetails?.name?.toLowerCase();
    const isOAuthApp =
      appName === "keeta" ||
      appName === "foodics" ||
      appName === "tabsense" ||
      appDetails?.appType === "TABSENSE";

    if (isOAuthApp || isNinjaAvailableApp(appDetails)) {
      return isValid;
    }

    // Validate credential fields for non-OAuth apps
    if (appDetails?.credentialsTemplate) {
      Object.keys(appDetails.credentialsTemplate).forEach((key) => {
        if (!appData[key] || appData[key].trim() === "") {
          const fieldName = key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase());
          errors[key] = `${fieldName} ${t("is required")}`;
          isValid = false;
        }
      });
    }

    // Only set field errors if there are any
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
    }

    return isValid && !storeError;
  };

  const fetchUserStores = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const fetchedStores = response.data.stores || [];
      const localizedStores = fetchedStores.map((store) => {
        const localizedName = isArabic
          ? store?.name?.ar || store?.name_ar || store?.name?.en || store?.name
          : store?.name?.en || store?.name;
        return {
          ...store,
          displayName: localizedName || store?.name,
        };
      });
      setStores(localizedStores);

      if (storeIdFromState) {
        const matchingStore = fetchedStores.find(
          (store) => store._id === storeIdFromState
        );
        if (matchingStore) {
          setSelectedStore(matchingStore._id);
        }
      }
    } catch (error) {
      console.error("Error fetching user stores:", error.message);
    }
  };

  const fetchBranches = async () => {
    try {
      setIsLoadingBranches(true);
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${selectedStore}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rawBranches = response.data.branches || [];
      const localizedBranches = rawBranches.map((branch) => ({
        ...branch,
        displayName: isArabic
          ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
          : branch?.name?.en || branch?.name,
      }));
      setBranches(localizedBranches);
      const partnerSession = readPartnerSession();
      const wantedId = String(partnerBranchId || partnerSession?.branchId || "").trim();
      if (wantedId) {
        const byId = localizedBranches.find((branch) => String(branch._id) === wantedId);
        if (byId) {
          setSelectedBranch(byId);
          return;
        }
      }
      const wanted = String(searchParams.get("branchName") || partnerSession?.branchName || "").trim().toLowerCase();
      if (wanted) {
        const match = localizedBranches.find((branch) => {
          const names = [branch.displayName, branch.name, branch.name_ar, branch?.name?.en, branch?.name?.ar]
            .filter(Boolean)
            .map((value) => String(value).trim().toLowerCase());
          return names.includes(wanted);
        });
        if (match) setSelectedBranch(match);
      }
    } catch (error) {
      console.error("Error fetching branches:", error.message);
    } finally {
      setIsLoadingBranches(false);
    }
  };

  const fetchAppDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/available-apps/${appId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAppDetails(response.data.app);
    } catch (error) {
      console.error("Error fetching app details:", error.message);
      toast.error(`${t("Failed to fetch app details")}`);
    }
  };

  const getFoodicsRedirectUri = () => {
    if (process.env.REACT_APP_FOODICS_REDIRECT_URI) {
      return process.env.REACT_APP_FOODICS_REDIRECT_URI;
    }

    const hostname = window?.location?.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${window.location.origin}/redirect-foodics`;
    }

    return 'https://onehub.staging.loops.sa/redirect-foodics';
  };

  const normalizeFoodicsOAuthUrl = (url) => {
    if (!url) return '';

    try {
      const parsed = new URL(url);
      const redirectUri = getFoodicsRedirectUri();
      const redirectParamNames = [
        'redirect_uri',
        'redirectUrl',
        'redirect_url',
        'callback_url',
        'callback',
      ];
      const existingParam = redirectParamNames.find((name) =>
        parsed.searchParams.has(name)
      );

      if (existingParam) {
        parsed.searchParams.set(existingParam, redirectUri);
      } else {
        parsed.searchParams.append('redirect_uri', redirectUri);
      }

      return parsed.toString();
    } catch (error) {
      console.warn('Unable to normalize Foodics OAuth URL:', error);
      return url;
    }
  };

  const handleKeetaOAuth = () => {
    if (!validateFields()) return;

    const applicationId = process.env.REACT_APP_KEETA_CLIENT_ID;
    const storeId = selectedStore;
    const appInternalId = appId;

    if (!storeId || !appInternalId) {
      toast.error(`${t("Please select a store and ensure app is loaded.")}`);
      return;
    }

    const redirectUri = process.env.REACT_APP_KEETA_REDIRECT_URI;
    const state = JSON.stringify({ storeId, appId: appInternalId });

    const keetaOAuthUrl =
      `https://merchant.mykeeta.com/m/web/openapi/authorize` +
      `?responseType=authorization_code` +
      `&appId=${encodeURIComponent(applicationId)}` +
      `&redirectUri=${encodeURIComponent(redirectUri)}` +
      `&state=${encodeURIComponent(state)}` +
      `&scope=all`;

    window.location.href = keetaOAuthUrl;
  };

  /**
   * First connect: state = JSON.stringify({ storeId, appId }) (same pattern as Keeta OAuth).
   * Re-auth: state = stored tid (unified) or unique_id (branch-oriented) from /tabsense/auth/reauth-context.
   * Expected state is saved to localStorage for CSRF verification on callback.
   */
  const handleTabSenseOAuth = async () => {
    if (!validateFields()) return;

    const storeId = selectedStore;
    const appInternalId = appId;

    if (!storeId || !appInternalId) {
      toast.error(`${t("Please select a store and ensure app is loaded.")}`);
      return;
    }

    const clientId =
      getTabSenseClientId() || appDetails?.credentialsTemplate?.client_id;
    if (!clientId) {
      toast.error(
        t("TabSense client ID is not configured (REACT_APP_TABSENSE_CLIENT_ID).")
      );
      return;
    }

    const redirectUri = getTabSenseRedirectUri();
    let oauthState = null;

    if (storeHasTabSenseIntegration) {
      try {
        const res = await api.get(`/tabsense/auth/reauth-context/${storeId}`);
        if (
          res.data?.success &&
          res.data?.data?.hasStoredTid &&
          res.data.data.oauthState != null &&
          res.data.data.oauthState !== ""
        ) {
          oauthState = String(res.data.data.oauthState);
        }
      } catch (e) {
        toast.error(
          translateError(e?.response?.data?.message) ||
            t("Failed to load TabSense re-authentication context")
        );
        return;
      }
    }

    if (oauthState == null) {
      oauthState = JSON.stringify({ storeId, appId: appInternalId });
    }

    try {
      localStorage.setItem('tabsense_oauth_state', oauthState);
      // So /oauth/tabsense/callback can return here if the user cancels TabSense or the flow aborts without appId in the API response.
      localStorage.setItem(
        'tabsense_oauth_context',
        JSON.stringify({ appId: appInternalId, storeId })
      );
    } catch {
      /* ignore */
    }

    const authorizeUrl = getTabSenseAuthorizeUrl();
    const tabSenseOAuthUrl =
      `${authorizeUrl}` +
      `?client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&state=${encodeURIComponent(oauthState)}`;

    window.location.href = tabSenseOAuthUrl;
  };

  const handleFoodicsOAuth = () => {
    if (!validateFields()) return;

    let foodicsOAuthUrl = "";
    if (appDetails?.credentialsTemplate?.install_url) {
      foodicsOAuthUrl = normalizeFoodicsOAuthUrl(appDetails.credentialsTemplate.install_url);
    }

    window.location.href = foodicsOAuthUrl;
  };

  const handleFoodicsSync = async () => {
    if (!validateFields()) return;

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem("token");
      const foodicsCredentialsId = appDetails?.foodicsCredentials?.[0]?.id;

      if (!foodicsCredentialsId) {
        toast.error(t("Foodics credentials not found"));
        return;
      }

      const payload = {
        storeId: selectedStore,
        foodicsCredentialsId,
      };

      await toast.promise(
        api.post("/v2/foodics/store/sync", payload, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        {
          pending: t("Syncing store with Foodics..."),
          success: t("Store synced successfully!"),
          error: {
            render({ data }) {
              const msg = data?.response?.data?.message;
              return (
                (msg && translateError(msg)) || `${t("Failed to sync store")}`
              );
            },
          },
        }
      );

      await fetchApps();
      await checkFoodicsIntegration();
      await fetchBranches();
      await fetchFoodicsOutlets(selectedStore);
    } catch (err) {
      const res = err?.response;
      if (res?.status === 422 && res?.data?.requiresReauthorization) {
        setTimeout(
          () => navigate("/apps-management", { replace: true }),
          3000
        );
      }
      console.error("Error syncing store:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    setError("");
    setSuccessMessage("");

    // First validate all fields
    if (!validateFields()) {
      if (!selectedStore && !isFoodicsCheck()) {
        setStoreError(t("Store is required"));
      }
      return;
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem("token");

      // normalize app name for comparison
      // Build credentials payload. Ninja is enabled with a switch and sends no secrets.
      let credentialsPayload = {};
      if (
        appDetails?.credentialsTemplate &&
        !isNinjaAvailableApp(appDetails)
      ) {
        Object.keys(appDetails.credentialsTemplate).forEach((key) => {
          credentialsPayload[key] = appData[key];
        });
      }
      // For ninja variants, credentialsPayload stays empty {}

      const payload = {
        appId,
        storeId: selectedStore,
        branchId: selectedBranch?._id || "",
        integrationType: "STORE",
        credentials: credentialsPayload, // always included (empty for ninja apps)
      };

      await toast.promise(
        api.post("/apps", payload, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        {
          pending: t("Integrating app..."),
          success: t("App integrated successfully!"),
          error: {
            render({ data }) {
              const body = data?.response?.data;
              return (
                subscriptionConnectErrorMessage(body, t)
                || (body?.message && translateError(body.message))
                || `${t("Failed to integrate app")}`
              );
            },
          },
        }
      );

      invalidateSetupGuideCache();
      setEditingCreds(false);
      await fetchApps();
      if (selectedStore) await fetchBranches();
      if (!returnAfterPartnerConnect(selectedBranch?._id) && readPartnerSession()?.id) {
        setTimeout(() => navigate("/apps-management"), 1500);
      }
    } catch (err) {
      console.error("Error integrating app:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (field, value) => {
    // Update the field value
    setAppData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setError("");

    // Validate the field immediately
    if (value && value.trim()) {
      // Clear error if field has value
      setFieldErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    } else {
      // Show error if field is empty
      const fieldName = field
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());

      setFieldErrors((prev) => ({
        ...prev,
        [field]: `${fieldName} ${t("is required")}`,
      }));
    }
  };

  const isAppSyncedWithBranch = (branch) => {
    if (isKeetaApp) {
      // For Keeta: just check if the branch has Keeta in syncedApps
      const hasKeeta = branch.syncedApps?.some(
        (app) => app.app?.toLowerCase() === "keeta"
      );
      return hasKeeta;
    }

    if (isChefzApp) {
      // For Chefz: check if the branch has properly synced Chefz (not just pending approval)
      // Only show "Integrated" if synced and NOT pending approval
      const chefzApp = branch.syncedApps?.find(
        (app) => app.app?.toLowerCase()?.includes("chefz")
      );
      
      if (!chefzApp) return false;
      
      // If branch has chefzShopId → it's just mapped, not integrated
      if (branch.chefzShopId && !chefzApp.external_id) return false;
      
      // If synced_status is true but external_id is null → pending approval, not integrated
      if (chefzApp.synced_status === true && chefzApp.external_id === null) return false;
      
      // Only integrated if synced_status is true AND has external_id (approved by platform)
      return chefzApp.synced_status === true && chefzApp.external_id !== null;
    }

    if (isTabSenseApp) {
      return branch.syncedApps?.some(
        (app) => app.app?.toLowerCase().includes("tabsense")
      );
    }

    const pageIsJahez = /jahez/i.test(
      `${appDetails?.name || ""} ${appDetails?.appType || ""} ${appDetails?.application || ""}`
    );
    if (pageIsJahez) {
      return branch.syncedApps?.some(
        (app) => /jahez/i.test(app.app || "") && app.synced_status === true
      );
    }

    // Default flow for other apps
    const relevantApps = branch.syncedApps?.filter(
      (app) => refId(app.appId) === refId(appDetails?._id)
    );
    if (!relevantApps || relevantApps.length === 0) return false;

    const latest = relevantApps.sort(
      (a, b) => new Date(b.synced_date) - new Date(a.synced_date)
    )[0];
    return latest?.synced_status === true;
  };

  const isKeetaApp = appDetails?.name?.toLowerCase() === "keeta";
  const isFoodicsApp = appDetails?.name?.toLowerCase() === "foodics";
  const isTabSenseApp =
    appDetails?.appType === "TABSENSE" ||
    appDetails?.name?.toLowerCase() === "tabsense";

  // Update Foodics auth status when appDetails changes
  useEffect(() => {
    if (isFoodicsApp && appDetails && appDetails.foodicsCredentials) {
      setIsFoodicsAuthed(true);
    } else {
      setIsFoodicsAuthed(false);
    }
  }, [appDetails, isFoodicsApp]);
  const isChefzApp = appDetails?.name?.toLowerCase().includes("chefz");
  const isNinjaApp = isNinjaAvailableApp(appDetails);
  const isJahezApp = /jahez/i.test(`${appDetails?.name || ""} ${appDetails?.appType || ""} ${appDetails?.application || ""}`);
  const isFlagApp = isNinjaApp || isJahezApp || (!isKeetaApp && !isFoodicsApp && !isTabSenseApp && !isChefzApp);

  const integrationForStore = (storeId) => apps.find((app) => {
    if (refId(app.store?._id || app.store) !== refId(storeId)) return false;
    if (String(app.status || "").toLowerCase() === "disconnected") return false;
    if (refId(app.availableAppId) === refId(appId)) return true;
    if (!isJahezApp) return false;
    return /jahez/i.test(`${app.application || ""} ${app.availableAppId?.name || ""}`);
  });

  const isIntegratedForSelectedStore = () => Boolean(integrationForStore(selectedStore));

  const handleDisconnectStore = async () => {
    const integration = integrationForStore(selectedStore);
    if (!integration?._id) return;
    setIsSubmitting(true);
    try {
      await api.delete(`/apps/${integration._id}`);
      toast.success(t("App disconnected successfully."));
      setEditingCreds(false);
      await fetchApps();
      if (selectedStore) await fetchBranches();
    } catch (err) {
      toast.error(err.response?.data?.message || t("Failed to disconnect app."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBranchFlag = async (branch, enabled) => {
    const integration = integrationForStore(selectedStore);
    if (!integration?._id || !branch?._id) return;
    setTogglingBranch(branch._id);
    try {
      await api.post("/sync-branch", {
        appId: integration._id,
        branchId: branch._id,
        status: enabled,
      });
      toast.success(enabled ? t("Branch synced successfully") : t("Branch unsynced successfully"));
      setConnectRefresh((value) => value + 1);
      await fetchBranches();
    } catch (err) {
      const data = err.response?.data;
      toast.error(
        subscriptionConnectErrorMessage(data, t)
          || (data?.message && !/^server error\b/i.test(data.message) ? translateError(data.message) : "")
          || t("Failed to update branch")
      );
    } finally {
      setTogglingBranch(null);
    }
  };

  const isAlreadyIntegrated = integratedAppIds.includes(appId);
  const enhancedBranches = branches.map((branch) => {
    const keetaApp = branch.syncedApps?.find(
      (app) => app.app === "Keeta" && app.external_id
    );
    const keetaShop = keetaShops.find(
      (shop) => shop.keetaShopId === keetaApp?.external_id
    );

    const foodicsApp = branch.syncedApps?.find(
      (app) => app.app === "Foodics" && app.external_id
    );
    const foodicsOutletFromSynced = foodicsOutlets.find(
      (o) => o.foodicsBranchId === foodicsApp?.external_id
    );
    const foodicsOutletFromField = foodicsOutlets.find(
      (o) => o._id === branch.foodicsBranchId
    );
    const selectedFoodicsId =
      foodicsOutletFromField?._id || foodicsOutletFromSynced?._id || "";

    const chefzApp = branch.syncedApps?.find((app) => {
      const appName = app.app?.toLowerCase();
      return (
        appName?.includes("chefz") && (app.external_id || app.appId)
      );
    });
    const chefzShopFromSynced = chefzShops.find((s) => {
      return (
        String(s.chefzShopId) ===
        String(chefzApp?.external_id || chefzApp?.appId)
      );
    });
    const chefzShopFromField = chefzShops.find((s) => {
      return String(s._id) === String(branch.chefzShopId);
    });
    const selectedChefzId =
      chefzShopFromSynced?._id || chefzShopFromField?._id || "";

    const tabSenseApp = branch.syncedApps?.find(
      (app) =>
        app.app?.toLowerCase().includes("tabsense") && app.external_id
    );
    const tabSenseShopFromSynced = tabSenseShops.find(
      (s) => String(s.tabsenseBranchId) === String(tabSenseApp?.external_id)
    );
    const tabSenseShopFromField = tabSenseShops.find(
      (s) => String(s._id) === String(branch.tabsenseShopId)
    );
    const selectedTabSenseShopId =
      tabSenseShopFromField?._id || tabSenseShopFromSynced?._id || "";

    // Check if branch is pending approval for Chefz
    // Fix: Differentiate between "mapped" vs "synced and pending approval"
    // Priority logic:
    // 1. If chefzShopId exists → show dropdown (mapped state)
    // 2. If only syncedApp with synced_status=true and NO chefzShopId → show pending (real sync)
    // 3. If both exist → prioritize showing dropdown (backend created syncedApp incorrectly during mapping)
    const isPendingApproval = chefzApp?.synced_status === true && 
                             chefzApp?.external_id === null && 
                             !branch.chefzShopId; // Only pending if no direct mapping exists

    // Debug logging
    console.log(`Branch ${branch.name || branch._id}:`, {
      chefzApp,
      chefzShopFromSynced,
      chefzShopFromField,
      selectedChefzId,
      isPendingApproval,
      branchChefzShopId: branch.chefzShopId,
      hasSyncedApp: !!chefzApp,
      syncedStatus: chefzApp?.synced_status,
      externalId: chefzApp?.external_id,
      logic: branch.chefzShopId ? 'MAPPED' : (chefzApp?.synced_status === true ? 'PENDING' : 'UNMAPPED')
    });

    return {
      ...branch,
      selectedKeetaShopId: keetaShop?._id || "",
      selectedFoodicsBranchId: selectedFoodicsId,
      selectedChefzShopId: selectedChefzId,
      selectedTabSenseShopId,
      isPendingApproval,
    };
  });
  const getAvailableKeetaShops = (keetaShops, branches, currentBranchId) => {
    const currentBranch = branches.find(
      (branch) => branch._id === currentBranchId
    );
    const currentBranchKeetaShopIds = new Set();

    if (currentBranch?.syncedApps?.length) {
      currentBranch.syncedApps.forEach((app) => {
        if (app.app === "Keeta" && app.external_id) {
          // Find the corresponding Keeta shop to get its _id
          const shop = keetaShops.find(
            (s) => s.keetaShopId === app.external_id
          );
          if (shop) {
            currentBranchKeetaShopIds.add(shop._id);
          }
        }
      });
    }

    const allLinkedKeetaShopIds = new Set();
    branches.forEach((branch) => {
      const syncedApps = branch.syncedApps || [];
      syncedApps.forEach((app) => {
        if (app.app === "Keeta" && app.external_id) {
          // Find the corresponding Keeta shop to get its _id
          const shop = keetaShops.find(
            (s) => s.keetaShopId === app.external_id
          );
          if (shop) {
            allLinkedKeetaShopIds.add(shop._id);
          }
        }
      });
    });

    return keetaShops.filter((shop) => {
      return (
        !allLinkedKeetaShopIds.has(shop._id) || // not linked in any branch
        currentBranchKeetaShopIds.has(shop._id) // or already linked in this branch
      );
    });
  };

  const getAvailableFoodicsOutlets = (
    foodicsOutlets,
    branches,
    currentBranchId
  ) => {
    const currentBranch = branches.find(
      (branch) => branch._id === currentBranchId
    );
    const currentBranchOutletIds = new Set();

    if (currentBranch?.syncedApps?.length) {
      currentBranch.syncedApps.forEach((app) => {
        if (app.app === "Foodics" && app.external_id) {
          // Find the corresponding Foodics outlet to get its _id
          const outlet = foodicsOutlets.find(
            (o) => o.foodicsBranchId === app.external_id
          );
          if (outlet) {
            currentBranchOutletIds.add(outlet._id);
          }
        }
      });
    }

    const allLinkedOutletIds = new Set();
    branches.forEach((branch) => {
      const syncedApps = branch.syncedApps || [];
      syncedApps.forEach((app) => {
        if (app.app === "Foodics" && app.external_id) {
          // Find the corresponding Foodics outlet to get its _id
          const outlet = foodicsOutlets.find(
            (o) => o.foodicsBranchId === app.external_id
          );
          if (outlet) {
            allLinkedOutletIds.add(outlet._id);
          }
        }
      });
    });

    return (foodicsOutlets || []).filter((outlet) => {
      return (
        !allLinkedOutletIds.has(outlet._id) || // not linked in any branch
        currentBranchOutletIds.has(outlet._id) // or already linked in this branch
      );
    });
  };

  const getAvailableTabSenseShops = (
    tabSenseShopList,
    branches,
    currentBranchId
  ) => {
    const currentBranch = branches.find(
      (branch) => branch._id === currentBranchId
    );
    const currentBranchTabSenseDocIds = new Set();

    if (currentBranch?.syncedApps?.length) {
      currentBranch.syncedApps.forEach((app) => {
        if (
          app.app?.toLowerCase().includes("tabsense") &&
          app.external_id
        ) {
          const shop = tabSenseShopList.find(
            (s) => String(s.tabsenseBranchId) === String(app.external_id)
          );
          if (shop) {
            currentBranchTabSenseDocIds.add(shop._id);
          }
        }
      });
    }

    const allLinkedTabSenseDocIds = new Set();
    branches.forEach((branch) => {
      const syncedApps = branch.syncedApps || [];
      syncedApps.forEach((app) => {
        if (
          app.app?.toLowerCase().includes("tabsense") &&
          app.external_id
        ) {
          const shop = tabSenseShopList.find(
            (s) => String(s.tabsenseBranchId) === String(app.external_id)
          );
          if (shop) {
            allLinkedTabSenseDocIds.add(shop._id);
          }
        }
      });
    });

    return (tabSenseShopList || []).filter((shop) => {
      return (
        !allLinkedTabSenseDocIds.has(shop._id) ||
        currentBranchTabSenseDocIds.has(shop._id)
      );
    });
  };

  const getAvailableChefzShops = (chefzShops, branches, currentBranchId) => {
    const currentBranch = branches.find(
      (branch) => branch._id === currentBranchId
    );
    const currentBranchShopIds = new Set();

    if (currentBranch?.syncedApps?.length) {
      currentBranch.syncedApps.forEach((app) => {
        const appName = app.app?.toLowerCase();
        const externalId = app.external_id || app.appId;
        if (appName?.includes("chefz") && externalId) {
          const shop = chefzShops.find((s) => {
            return String(s.chefzShopId) === String(externalId);
          });
          if (shop) {
            currentBranchShopIds.add(shop._id);
          }
        }
      });
    }

    const allLinkedShopIds = new Set();
    branches.forEach((branch) => {
      const syncedApps = branch.syncedApps || [];
      syncedApps.forEach((app) => {
        const appName = app.app?.toLowerCase();
        const externalId = app.external_id || app.appId;
        if (appName?.includes("chefz") && externalId) {
          const shop = chefzShops.find((s) => {
            return String(s.chefzShopId) === String(externalId);
          });
          if (shop) {
            allLinkedShopIds.add(shop._id);
          }
        }
      });
    });

    return chefzShops.filter((shop) => {
      return (
        !allLinkedShopIds.has(shop._id) || // not linked in any branch
        currentBranchShopIds.has(shop._id) // or already linked in this branch
      );
    });
  };
  const tableColumns = [
    {
      key: "name",
      header: `${t("addAppPage.addAppTable.branchName")}`,
      render: (value, row) => {
        const nameVal = typeof value === "object" ? value : row?.name;
        const localized = isArabic
          ? nameVal?.ar || row?.name_ar || nameVal?.en || nameVal
          : nameVal?.en || nameVal;
        const label = localized || "-";
        if (!isFlagApp) return <TableCellEllipsis title={label}>{label}</TableCellEllipsis>;
        const integrationId = row.integrationId && row.integrationId !== "-" ? row.integrationId : "";
        return (
          <div className="aa-bn">
            <span>{label}</span>
            {integrationId ? (
              <span className="aa-idbox">
                <span className="aa-idl">ID</span>
                <code>{integrationId}</code>
                <button
                  type="button"
                  className="aa-copybtn"
                  title={t("addAppPage.copyIntegrationId")}
                  aria-label={t("addAppPage.copyIntegrationIdFor", { branch: label })}
                  onClick={() => {
                    navigator.clipboard.writeText(integrationId);
                    setCopiedId(integrationId);
                    setTimeout(() => setCopiedId(null), 2000);
                  }}
                >
                  {copiedId === integrationId ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                </button>
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "status",
      header: `${t("addAppPage.addAppTable.status")}`,
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (value, row) => {
        const storeOn = Boolean(integrationForStore(selectedStore));
        const pending = Boolean(row.isPendingApproval);
        const synced = isAppSyncedWithBranch(row);
        const label = !storeOn
          ? t("addAppPage.storeNotConnectedStatus")
          : pending
            ? t("addAppPage.addAppTable.pendingApproval")
            : synced
              ? t("addAppPage.storeConnected")
              : t("addAppPage.storeNotConnected");
        const tone = !storeOn || (!pending && !synced) ? "off" : pending ? "wait" : "ok";
        const leaving = (connectInfo?.marked || []).some((mark) => (
          String(mark.branchId) === String(row._id)
          || (mark.branchIds || []).some((id) => String(id) === String(row._id))
        ));
        const renews = connectInfo?.renewsAt ? new Date(connectInfo.renewsAt) : null;
        const renewLabel = renews && !Number.isNaN(renews.getTime()) ? renews.toLocaleDateString("en-GB") : "";
        return (
          <div>
            <span className={`aa-spill ${tone}`}>{label}</span>
            {synced && leaving && renewLabel ? (
              <span className="aa-tag is-less">{t("addAppPage.disconnectsOn", { date: renewLabel })}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "updatedAt",
      header: `${t("addAppPage.addAppTable.lastUpdated")}`,
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (value, row) => {
        const relevantApps = row.syncedApps?.filter(
          (app) => app.appId === appDetails?._id
        );
        const latest = relevantApps?.sort(
          (a, b) => new Date(b.synced_date) - new Date(a.synced_date)
        )[0];
        const dateToDisplay = latest?.synced_date || value;

        if (!dateToDisplay) return "-";

        const date = new Date(dateToDisplay);
        return (
          <div className="oh-table-date-cell text-nowrap" dir="ltr">
            <span className="oh-table-date-cell__date">
              {date.toLocaleDateString("en-GB")}
            </span>
            <span className="oh-table-date-cell__time">
              {date.toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}
            </span>
          </div>
        );
      },
    },

    ...(appDetails && !isKeetaApp && !isFoodicsApp && !isTabSenseApp && !isChefzApp
      ? [
          {
            key: "enable",
            header: t("addAppPage.enable"),
            headerClassName: "text-center",
            cellClassName: "text-center",
            render: (_value, row) => {
              const on = isAppSyncedWithBranch(row);
              const atBranchLimit = Boolean(
                connectInfo?.limit > 0 && connectInfo.used >= connectInfo.limit && !on
              );
              const locked = !integrationForStore(selectedStore) || atBranchLimit;
              const nameVal = typeof row?.name === "object" ? row.name : row?.name;
              const branchLabel = (isArabic ? nameVal?.ar || row?.name_ar || nameVal?.en : nameVal?.en) || nameVal || "";
              return (
                <label className={`aa-switch${locked ? " is-ro" : ""}`}>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={on}
                    disabled={locked || togglingBranch === row._id}
                    aria-label={t("addAppPage.enableBranch", { branch: branchLabel })}
                    onChange={(event) => handleBranchFlag(row, event.target.checked)}
                  />
                  <span className="track" aria-hidden="true" />
                  <span className="aa-swl">{on ? t("addAppPage.enabled") : t("addAppPage.off")}</span>
                </label>
              );
            },
          },
        ]
      : []),
    ...(appDetails && isKeetaApp
      ? [
          {
            key: "keetaShop",
            header: `${t("addAppPage.addAppTable.syncKeetaShop")}`,
            headerClassName: "",
            cellClassName: "text-center",
            render: (value, row) => {
              const availableKeetaShops = getAvailableKeetaShops(
                keetaShops,
                branches,
                row._id
              );

              const handleSelect = async (e) => {
                const selectedShopId = e.target.value;
                const token = localStorage.getItem("token");

                try {
                  // Get the current branch data
                  const { data: fullBranch } = await api.get(
                    `/stores/${selectedStore}/branch/${row._id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );

                  // Prepare payload - remove internal fields
                  const {
                    _id,
                    syncedApps,
                    createdAt,
                    updatedAt,
                    __v,
                    ...cleanBranch
                  } = fullBranch;

                  let payload = { ...cleanBranch };

                  // Only add keetaShopId if a shop is selected
                  if (selectedShopId) {
                    // Find the selected Keeta shop to get its _id (ObjectId)
                    const selectedKeetaShop = keetaShops.find(
                      (shop) => shop._id === selectedShopId
                    );

                    if (!selectedKeetaShop) {
                      toast.error(t("Selected Keeta shop not found"));
                      return;
                    }

                    payload.keetaShopId = selectedKeetaShop._id;
                  } else {
                    // Explicitly set to empty or null if no shop is selected
                    payload.keetaShopId = null; // or "" depending on what your backend expects
                  }

                  await api.put(
                    `/stores/${selectedStore}/branch/${row._id}`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );

                  toast.success(t("Branch updated successfully"));
                  await fetchBranches(); // Refresh branches data
                } catch (err) {
                  toast.error(
                    branchUpdateErrorMessage(err) || t("Failed to update branch")
                  );
                  console.error("Update error:", err);
                }
              };

              // Get the current value for this specific branch
              const currentValue = row.selectedKeetaShopId || "";

              return (
                <div
                  className="keeta-dropdown-wrapper"
                  style={{ position: "relative" }}
                >
                  <select
                    className="keeta-dropdown"
                    value={currentValue}
                    onChange={handleSelect}
                    disabled={!availableKeetaShops.length || !storeHasKeetaIntegration}
                    style={{
                      width: "100%",
                      appearance: "none",
                      paddingRight: "2rem",
                    }}
                  >
                    <option value="" disabled>
                      {availableKeetaShops.length
                        ? `${t("addAppPage.addAppTable.dropdownPlaceHolder")}`
                        : `${t("addAppPage.addAppTable.dropdownPlaceHolder2")}`}
                    </option>
                    {availableKeetaShops.map((shop) => (
                      <option key={shop._id} value={shop._id}>
                        {shop.keetaShopId} - {shop.shopName}
                      </option>
                    ))}
                  </select>

                  {currentValue ? (
                    <X
                      size={16}
                      onClick={() => handleSelect({ target: { value: "" } })}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#888",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        color: "#888",
                      }}
                    />
                  )}
                </div>
              );
            },
          },
        ]
      : []),
    ...(appDetails && isFoodicsApp
      ? [
          {
            key: "foodicsOutlet",
            header: `${t("addAppPage.addAppTable.syncFoodicsOutlet")}`,
            headerClassName: "",
            cellClassName: "text-center",
            render: (value, row) => {
              const availableFoodicsOutlets = getAvailableFoodicsOutlets(
                foodicsOutlets,
                branches,
                row._id
              );

              const handleSelect = async (e) => {
                console.log("e is sknk", e);
                const selectedOutletId = e.target.value;
                const branchPosConflictKey = branchPosConflictTooltipKey(
                  row,
                  "Foodics"
                );
                if (branchPosConflictKey && selectedOutletId) {
                  toast.error(t(branchPosConflictKey));
                  return;
                }
                console.log(selectedOutletId);
                const token = localStorage.getItem("token");

                try {
                  const { data: fullBranch } = await api.get(
                    `/stores/${selectedStore}/branch/${row._id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );

                  const {
                    _id,
                    syncedApps,
                    createdAt,
                    updatedAt,
                    __v,
                    ...cleanBranch
                  } = fullBranch;

                  let payload = { ...cleanBranch };

                  if (selectedOutletId) {
                    payload.foodicsBranchId = selectedOutletId;
                  } else {
                    payload.foodicsBranchId = null;
                  }
                  omitOppositePosShopIdsFromBranchPayload(payload, "foodics");

                  await api.put(
                    `/stores/${selectedStore}/branch/${row._id}`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );

                  toast.success(t("Branch updated successfully"));
                  await fetchBranches();
                } catch (err) {
                  toast.error(
                    branchUpdateErrorMessage(err) || t("Failed to update branch")
                  );
                }
              };

              const currentValue = row.selectedFoodicsBranchId || "";
              const branchPosConflictKey = branchPosConflictTooltipKey(
                row,
                "Foodics"
              );
              const branchPosMappingBlocked = !!branchPosConflictKey;

              return wrapPosConflictTooltip(
                <div
                  className="keeta-dropdown-wrapper"
                  style={{ position: "relative" }}
                  title={
                    branchPosMappingBlocked
                      ? t(branchPosConflictKey)
                      : undefined
                  }
                >
                  <select
                    className="keeta-dropdown"
                    value={currentValue}
                    onChange={handleSelect}
                    disabled={
                      !availableFoodicsOutlets.length || branchPosMappingBlocked || !storeHasFoodicsIntegration
                    }
                    style={{
                      width: "100%",
                      appearance: "none",
                      paddingRight: "2rem",
                    }}
                  >
                    <option value="" disabled>
                      {availableFoodicsOutlets.length
                        ? `${t("addAppPage.addAppTable.dropdownPlaceHolder")}`
                        : `${t("addAppPage.addAppTable.dropdownPlaceHolder2")}`}
                    </option>
                    {availableFoodicsOutlets.map((outlet) => {
                      const oid = outlet._id || outlet.id;
                      const branchId =
                        outlet.foodicsBranchId || outlet.branchId || "N/A";
                      const displayName =
                        outlet.name ||
                        outlet.branchName ||
                        outlet.fullName ||
                        outlet.outletName ||
                        "Outlet";
                      return (
                        <option key={oid} value={oid}>
                          {`${branchId} - ${displayName}`}
                        </option>
                      );
                    })}
                  </select>

                  {currentValue ? (
                    <X
                      size={16}
                      onClick={() => handleSelect({ target: { value: "" } })}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#888",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        color: "#888",
                      }}
                    />
                  )}
                </div>,
                branchPosConflictKey
              );
            },
          },
        ]
      : []),
    ...(appDetails && isTabSenseApp
      ? [
          {
            key: "tabSenseBranch",
            header: `${t("addAppPage.addAppTable.syncTabSenseBranch")}`,
            headerClassName: "",
            cellClassName: "text-center",
            render: (value, row) => {
              const availableTabSenseShops = getAvailableTabSenseShops(
                tabSenseShops,
                branches,
                row._id
              );

              const handleSelect = async (e) => {
                const selectedShopDocId = e.target.value;
                const branchPosConflictKey = branchPosConflictTooltipKey(
                  row,
                  "TabSense"
                );
                if (branchPosConflictKey && selectedShopDocId) {
                  toast.error(t(branchPosConflictKey));
                  return;
                }
                const token = localStorage.getItem("token");

                try {
                  const { data: fullBranch } = await api.get(
                    `/stores/${selectedStore}/branch/${row._id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );

                  const {
                    _id,
                    syncedApps,
                    createdAt,
                    updatedAt,
                    __v,
                    ...cleanBranch
                  } = fullBranch;

                  let payload = { ...cleanBranch };

                  if (selectedShopDocId) {
                    payload.tabsenseShopId = selectedShopDocId;
                  } else {
                    payload.tabsenseShopId = null;
                  }
                  omitOppositePosShopIdsFromBranchPayload(payload, "tabsense");

                  await api.put(
                    `/stores/${selectedStore}/branch/${row._id}`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );

                  toast.success(t("Branch updated successfully"));
                  await fetchBranches();
                } catch (err) {
                  toast.error(
                    branchUpdateErrorMessage(err) || t("Failed to update branch")
                  );
                }
              };

              const currentValue = row.selectedTabSenseShopId || "";
              const branchPosConflictKey = branchPosConflictTooltipKey(
                row,
                "TabSense"
              );
              const branchPosMappingBlocked = !!branchPosConflictKey;

              return wrapPosConflictTooltip(
                <div
                  className="keeta-dropdown-wrapper"
                  style={{ position: "relative" }}
                  title={
                    branchPosMappingBlocked
                      ? t(branchPosConflictKey)
                      : undefined
                  }
                >
                  <select
                    className="keeta-dropdown"
                    value={currentValue}
                    onChange={handleSelect}
                    disabled={
                      !availableTabSenseShops.length || branchPosMappingBlocked || !storeHasTabSenseIntegration
                    }
                    style={{
                      width: "100%",
                      appearance: "none",
                      paddingRight: "2rem",
                    }}
                  >
                    <option value="" disabled>
                      {availableTabSenseShops.length
                        ? `${t("addAppPage.addAppTable.dropdownPlaceHolder")}`
                        : `${t("addAppPage.addAppTable.dropdownPlaceHolder2")}`}
                    </option>
                    {availableTabSenseShops.map((shop) => {
                      const sid = shop._id || shop.id;
                      const extId = shop.tabsenseBranchId || "N/A";
                      const displayName =
                        shop.name ||
                        shop.shopName ||
                        shop.branchName ||
                        "Branch";
                      return (
                        <option key={sid} value={sid}>
                          {`${extId} - ${displayName}`}
                        </option>
                      );
                    })}
                  </select>

                  {currentValue ? (
                    <X
                      size={16}
                      onClick={() => handleSelect({ target: { value: "" } })}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#888",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        color: "#888",
                      }}
                    />
                  )}
                </div>,
                branchPosConflictKey
              );
            },
          },
        ]
      : []),
    ...(appDetails && isChefzApp
      ? [
          {
            key: "chefzShop",
            header: `${t("addAppPage.addAppTable.syncChefzShop")}`,
            headerClassName: "",
            cellClassName: "",
            render: (value, row) => {
              // Check if branch is pending approval
              if (row.isPendingApproval) {
                return (
                  <span
                    style={{
                      color: "",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    {t("addAppPage.addAppTable.pendingApproval")}
                  </span>
                );
              }

              const availableChefzShops = getAvailableChefzShops(
                chefzShops,
                branches,
                row._id
              );

              const handleSelect = async (e) => {
                const selectedShopId = e.target.value;
                const token = localStorage.getItem("token");

                try {
                  const { data: fullBranch } = await api.get(
                    `/stores/${selectedStore}/branch/${row._id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );

                  const {
                    _id,
                    syncedApps,
                    createdAt,
                    updatedAt,
                    __v,
                    ...cleanBranch
                  } = fullBranch;

                  let payload = { ...cleanBranch };

                  // Only include chefzShopId if mapping a new shop OR if branch already
                  // has chefz mapped (to update or clear it). Omitting it when neither
                  // is true avoids a backend warning when the store has no Chefz AppIntegration.
                  const hadChefzMapped = syncedApps?.some(
                    (sa) => sa.app?.toLowerCase().includes("chefz") && sa.external_id
                  );
                  if (selectedShopId) {
                    payload.chefzShopId = selectedShopId;
                  } else if (hadChefzMapped) {
                    payload.chefzShopId = null;
                  }

                  await api.put(
                    `/stores/${selectedStore}/branch/${row._id}`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );

                  toast.success(t("Branch updated successfully"));
                  await fetchBranches();
                } catch (err) {
                  toast.error(
                    branchUpdateErrorMessage(err) || t("Failed to update branch")
                  );
                }
              };

              const currentValue = row.selectedChefzShopId || "";

              console.log(`Rendering dropdown for branch ${row.name || row._id}:`, {
                currentValue,
                availableShopsCount: availableChefzShops.length,
                availableShops: availableChefzShops.map(s => ({ _id: s._id, chefzShopId: s.chefzShopId, name: s.name }))
              });

              return (
                <div
                  key={`chefz-dropdown-${row._id}`}
                  className="keeta-dropdown-wrapper"
                  style={{ position: "relative" }}
                >
                  <select
                    className="keeta-dropdown"
                    value={currentValue}
                    onChange={handleSelect}
                    disabled={!availableChefzShops.length || !storeHasChefzIntegration}
                    style={{
                      width: "100%",
                      appearance: "none",
                      paddingRight: "2rem",
                    }}
                  >
                    <option value="">
                      {availableChefzShops.length
                        ? `${t("addAppPage.addAppTable.dropdownPlaceHolder")}`
                        : `${t("addAppPage.addAppTable.dropdownPlaceHolder2")}`}
                    </option>
                    {availableChefzShops.map((shop) => {
                      const sid = shop._id || shop.id;
                      const shopId = shop.chefzShopId || "N/A";
                      const displayName = shop.name || shop.shopName || "Shop";
                      return (
                        <option key={sid} value={sid}>
                          {`${shopId} - ${displayName}`}
                        </option>
                      );
                    })}
                  </select>

                  {currentValue ? (
                    <X
                      size={16}
                      onClick={() => handleSelect({ target: { value: "" } })}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#888",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        color: "#888",
                      }}
                    />
                  )}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  useEffect(() => {
    setBranchCurrentPage(1);
  }, [selectedStore, branches.length]);

  const getBranchSortValue = useCallback(
    (row, column) => {
      switch (column) {
        case "name": {
          const nameVal = typeof row?.name === "object" ? row.name : row?.name;
          const localized = isArabic
            ? nameVal?.ar || row?.name_ar || nameVal?.en || nameVal
            : nameVal?.en || nameVal;
          return (localized || row?.displayName || "").toString().toLowerCase();
        }
        case "status":
          return isAppSyncedWithBranch(row) ? 1 : 0;
        case "updatedAt": {
          const relevantApps = row.syncedApps?.filter(
            (app) => app.appId === appDetails?._id
          );
          const latest = relevantApps?.sort(
            (a, b) => new Date(b.synced_date) - new Date(a.synced_date)
          )[0];
          const dateToDisplay = latest?.synced_date || row.updatedAt;
          return dateToDisplay ? new Date(dateToDisplay).getTime() : 0;
        }
        case "enable":
          return isAppSyncedWithBranch(row) ? 1 : 0;
        default:
          return "";
      }
    },
    [isArabic, appDetails?._id, isKeetaApp, isChefzApp, isTabSenseApp, isFoodicsApp]
  );

  const sortedBranches = useMemo(
    () =>
      clientSortRows(
        enhancedBranches,
        branchTableSortColumn,
        branchSortDirection,
        getBranchSortValue
      ),
    [enhancedBranches, branchTableSortColumn, branchSortDirection, getBranchSortValue]
  );

  const paginatedBranches = useMemo(() => {
    const start = (branchCurrentPage - 1) * branchPageSize;
    return sortedBranches.slice(start, start + branchPageSize);
  }, [sortedBranches, branchCurrentPage, branchPageSize]);

  const applyBranchTableSort = useCallback((column, direction) => {
    setBranchTableSortColumn(column);
    setBranchSortDirection(direction);
    setBranchCurrentPage(1);
  }, []);

  const branchTableColumns = useMemo(() => {
    const sortableKeys = new Set(["name", "status", "updatedAt"]);
    const widthByKey = {
      name: { width: 280, minWidth: 220, ellipsis: false },
      status: { width: 190, minWidth: 160 },
      updatedAt: { width: 156, minWidth: 140 },
      enable: { width: 160, minWidth: 140, align: "center" },
      keetaShop: { width: 260, minWidth: 240 },
      foodicsOutlet: { width: 260, minWidth: 240 },
      tabSenseBranch: { width: 260, minWidth: 240 },
      chefzShop: { width: 260, minWidth: 240 },
    };

    return tableColumns.map((col) => {
      const sizing = widthByKey[col.key] || { minWidth: 160 };
      const align =
        sizing.align ||
        (col.cellClassName?.includes("text-center") ? "center" : undefined);

      return {
        id: col.key,
        header: col.header,
        headerClassName: col.headerClassName,
        align,
        sortable: sortableKeys.has(col.key),
        ...sizing,
        render: col.render
          ? (row) => col.render(row[col.key], row)
          : (row) => row[col.key] ?? "-",
      };
    });
  }, [tableColumns]);

  useEffect(() => {
    setEditingCreds(false);
  }, [selectedStore]);

  const handleConnectInfo = useCallback((next) => {
    setConnectInfo(next);
  }, []);

  const appDisplayName = (isArabic && appDetails?.name_ar) || appDetails?.name || "";
  const kindLabel = (isFoodicsApp || isTabSenseApp)
    ? t("addAppPage.kindPos")
    : t("addAppPage.kindIntegration");
  const branchModeHint = isChefzApp
    ? t("addAppPage.approveHint", { app: appDisplayName })
    : (isKeetaApp || isFoodicsApp || isTabSenseApp)
      ? t("addAppPage.mapHint", { app: appDisplayName })
      : t("addAppPage.flagHint", { app: appDisplayName });
  const credKeys = Object.keys(appDetails?.credentialsTemplate || {}).filter((key) => !/install_url|redirect/i.test(key));
  const publicCredKey = credKeys.find((key) => !/secret|password|token/i.test(key));
  const maskCred = (value) => {
    const text = String(value || "");
    if (text.length <= 8) return text;
    return `${text.slice(0, 6)}…${text.slice(-3)}`;
  };
  const credLabel = (key) => ({
    client_id: t("addAppPage.clientId"),
    client_secret: t("addAppPage.clientSecret"),
    clientId: t("addAppPage.clientId"),
    clientSecret: t("addAppPage.clientSecret"),
    username: t("addAppPage.username"),
    password: t("addAppPage.password"),
    user: t("addAppPage.username"),
    pass: t("addAppPage.password"),
  }[key] || t(key));
  const integrationDesc = isNinjaApp
    ? t("addAppPage.ninjaDesc")
    : isKeetaApp
      ? t("addAppPage.keetaConnectDesc")
      : isFoodicsApp
        ? t("addAppPage.foodicsConnectDesc")
        : isTabSenseApp
          ? t("addAppPage.tabSenseConnectDesc")
          : isChefzApp
            ? t("addAppPage.chefzDesc")
            : isJahezApp
              ? t("addAppPage.jahezDesc")
              : t("addAppPage.genericDesc", { app: appDisplayName });
  const headerLogo = resolveIntegrationLogo(appDetails);
  const connectLabel = isFoodicsApp && isFoodicsAuthed
    ? t("Sync Store")
    : t("addAppPage.connectWith", { app: appDisplayName });
  const startConnect = () => {
    if (isKeetaApp) handleKeetaOAuth();
    else if (isTabSenseApp) handleTabSenseOAuth();
    else if (isFoodicsApp && !isFoodicsAuthed) handleFoodicsOAuth();
    else if (isFoodicsApp) handleFoodicsSync();
    else handleSave();
  };
  const selectedStoreRecord = stores.find((store) => store._id === selectedStore);
  const connectedStoreCount = stores.filter((store) => integrationForStore(store._id)).length;
  const selectedStoreConnected = Boolean(selectedStore && integrationForStore(selectedStore));
  const connectedBranchCount = branches.filter((branch) => isAppSyncedWithBranch(branch)).length;
  const showCredentialForm = !isNinjaApp && !isKeetaApp && !isTabSenseApp && !isFoodicsApp && (!selectedStoreConnected || editingCreds);
  const showOAuthButton = (isKeetaApp || isTabSenseApp || isFoodicsApp) && (!selectedStoreConnected || editingCreds);

  return (
    <div className="aa-page sf-page">
      <button type="button" className="sf-btn link aa-back" onClick={() => navigate("/apps-management")}>
        ← {t("addAppPage.backToApps")}
      </button>
      <div className="aa-title">
        {headerLogo ? (
          <img
            className="aa-logo"
            src={headerLogo}
            alt=""
            onError={(event) => handlePlatformLogoError(event, appDetails)}
          />
        ) : (
          <span className="aa-logo aa-logo-fallback">{appDisplayName.slice(0, 1)}</span>
        )}
        <div>
          <h1>
            {appDisplayName}
            {appDetails?.isDemo ? <Badge color="warning" pill className="ms-2">Demo</Badge> : null}
          </h1>
          <p>{kindLabel}</p>
        </div>
      </div>
      {readPartnerSession()?.id ? (
        <div className="mb-3" style={{ display: "grid", gap: 8 }}>
          {readPartnerSession().branchName ? (
            <p className="mb-0">{t("partnerSessionBranchHint", { branch: readPartnerSession().branchName })}</p>
          ) : null}
          <Button
            color="secondary"
            outline
            onClick={() => {
              cancelPartnerSession().catch((err) => toast.error(err.response?.data?.message || err.message));
            }}
          >
            {t("partnerSessionNoAccount", { channel: readPartnerSession().channelName || "" })}
          </Button>
        </div>
      ) : null}

      <section className="sf-section">
        <div className="sf-section-head">
          <h2>{t("addAppPage.storesTitle")}</h2>
          <span className="sf-meta">{t("addAppPage.storesMeta", { connected: connectedStoreCount, total: stores.length })}</span>
        </div>
        <p className="sf-sub">{t("addAppPage.storesLead")}</p>
        <div className="aa-stores" role="group" aria-label={t("addAppPage.storesTitle")}>
          {stores.map((store) => {
            const on = Boolean(integrationForStore(store._id));
            const branchList = Array.isArray(store.branches) ? store.branches : [];
            const count = branchList.length || null;
            const branchObjects = branchList.filter((branch) => branch && typeof branch === "object" && branch.syncedApps);
            const usedHere = store._id === selectedStore
              ? connectedBranchCount
              : branchObjects.length
                ? branchObjects.filter((branch) => isAppSyncedWithBranch(branch)).length
                : null;
            return (
              <button
                type="button"
                key={store._id}
                className={`aa-store${store._id === selectedStore ? " is-on" : ""}`}
                aria-pressed={store._id === selectedStore}
                onClick={() => handleStoreSelect(store)}
              >
                <span className="top"><b>{storeTitle(store)}</b><span className="aa-radio" aria-hidden="true" /></span>
                <span className={`aa-tag${on ? " is-on" : ""}`}>{on ? t("addAppPage.storeConnected") : t("addAppPage.storeNotConnected")}</span>
                <small>
                  {count == null
                    ? (on && usedHere != null ? t("addAppPage.connectedHere", { used: usedHere, app: appDisplayName }) : "")
                    : on
                      ? t("addAppPage.storeBranchUsed", { count, used: usedHere ?? 0, app: appDisplayName })
                      : t("addAppPage.storeBranchCount", { count })}
                </small>
              </button>
            );
          })}
          <button type="button" className="aa-store aa-store-add" onClick={() => handleStoreSelect({ _id: "__CREATE_STORE__" })}>
            <b>{t("addAppPage.createStore")}</b>
          </button>
        </div>
        {storeError ? <p className="sf-error">{storeError}</p> : null}
      </section>

      <section className="sf-section">
        <div className="sf-section-head">
          <h2>{t("addAppPage.settingsTitle")}</h2>
          <span className="sf-meta">{storeTitle(selectedStoreRecord) || t("addAppPage.storeDropDownLabel")}</span>
        </div>
        <div className="aa-integ">
          <div className="sf-section-head">
            <h3>{t("addAppPage.storeIntegration", { store: storeTitle(selectedStoreRecord) || "—" })}</h3>
            <span className={`aa-tag${selectedStoreConnected ? " is-on" : isSubmitting && showOAuthButton ? " is-less" : ""}`}>
              {selectedStoreConnected
                ? t("addAppPage.storeConnected")
                : isSubmitting && (isKeetaApp || isTabSenseApp || isFoodicsApp)
                  ? t("addAppPage.waitingFor", { app: appDisplayName })
                  : t("addAppPage.storeNotConnected")}
            </span>
          </div>
          <p className="aa-desc">{integrationDesc}</p>
          {isNinjaApp ? (
            <div className="aa-togglerow">
              <span>
                <b>{t("addAppPage.ninjaEnable", { app: appDisplayName, store: storeTitle(selectedStoreRecord) || t("addAppPage.storeDropDownLabel") })}</b>
                <small>{selectedStoreConnected ? t("addAppPage.ninjaOn") : t("addAppPage.ninjaOff")}</small>
              </span>
              <label className="aa-switch">
                <input
                  type="checkbox"
                  role="switch"
                  checked={selectedStoreConnected}
                  disabled={isSubmitting || !selectedStore}
                  aria-label={t("addAppPage.ninjaEnable", { app: appDisplayName, store: storeTitle(selectedStoreRecord) })}
                  onChange={(event) => {
                    if (event.target.checked) handleSave();
                    else handleDisconnectStore();
                  }}
                />
                <span className="track" aria-hidden="true" />
                <span className="aa-swl">{selectedStoreConnected ? t("addAppPage.on") : t("addAppPage.off")}</span>
              </label>
            </div>
          ) : selectedStoreConnected && !editingCreds ? (
            <div className="aa-row-end">
              <small>
                {(isKeetaApp || isTabSenseApp || isFoodicsApp)
                  ? t("addAppPage.authorizedThrough", { app: appDisplayName })
                  : publicCredKey && appData[publicCredKey]
                    ? t("addAppPage.credSaved", { label: credLabel(publicCredKey), value: maskCred(appData[publicCredKey]) })
                    : ""}
              </small>
              <span className="aa-actions">
                <button type="button" className="sf-btn" onClick={handleDisconnectStore} disabled={isSubmitting}>{t("addAppPage.disconnectStore")}</button>
              </span>
            </div>
          ) : showCredentialForm ? (
            <>
              <div className="aa-cred">
                {credKeys.map((key) => (
                  <label key={key}>
                    <span>{credLabel(key)}</span>
                    <input
                      type={/secret|password/i.test(key) ? "password" : "text"}
                      autoComplete="off"
                      value={appData[key] || ""}
                      onChange={(event) => handleFieldChange(key, event.target.value)}
                      aria-invalid={fieldErrors[key] ? "true" : undefined}
                    />
                  </label>
                ))}
              </div>
              {Object.values(fieldErrors).some(Boolean) ? (
                <p className="aa-bad" role="alert">{Object.values(fieldErrors).filter(Boolean).join(" ")}</p>
              ) : null}
              <div>
                <button type="button" className="sf-btn navy" onClick={handleSave} disabled={isSubmitting || !selectedStore}>
                  {isSubmitting ? t("Integrating app...") : selectedStoreConnected ? t("addAppPage.updateIntegration") : t("addAppPage.connectStore")}
                </button>
              </div>
            </>
          ) : (
            <div>
              <button type="button" className="sf-btn navy" onClick={startConnect} disabled={isSubmitting || !selectedStore}>
                {isSubmitting ? t("addAppPage.waitingFor", { app: appDisplayName }) : connectLabel}
              </button>
              {isFoodicsApp && isFoodicsAuthed && appDetails?.foodicsCredentials?.[0]?.businessName ? (
                <p className="aa-desc">{t("Business")}: {appDetails.foodicsCredentials[0].businessName}</p>
              ) : null}
            </div>
          )}
        </div>
        {(isNinjaApp || isJahezApp) ? (
          <div className="aa-integ">
            <div className="sf-section-head">
              <h3>{t("addAppPage.enableBranches")}</h3>
              <span className="sf-hint">{t("addAppPage.enableBranchesHint")}</span>
            </div>
            <ol className="aa-stepstrip">
              <li><span className="n">1</span><span>{t("addAppPage.ninjaStep1")}</span></li>
              <li><span className="n">2</span><span>{isJahezApp ? t("addAppPage.jahezStep2") : t("addAppPage.ninjaStep2")}</span></li>
              <li><span className="n">3</span><span>{t("addAppPage.ninjaStep3")}</span></li>
            </ol>
          </div>
        ) : null}
        <p className="sf-hint">{t("addAppPage.otherSettings", { app: appDisplayName })}</p>
      </section>

      <section className="sf-section aa-bsec">
        <div className="aa-bhead">
          <div>
            <h2>{t("addAppPage.branchesTitle")}</h2>
            <p className="aa-bsub"><b>{storeTitle(selectedStoreRecord) || t("addAppPage.storeDropDownLabel")}</b> · {branchModeHint}</p>
          </div>
          <SubscriptionConnectBanner appId={appId} variant="meter" refreshKey={connectRefresh} onInfo={handleConnectInfo} />
        </div>
        {!selectedStoreConnected && selectedStore ? (
          <div className="aa-bnote" role="status">
            <p><b>{t("addAppPage.connectStoreFirst", { store: storeTitle(selectedStoreRecord), app: appDisplayName })}</b></p>
          </div>
        ) : null}
        <div className={selectedStore && !selectedStoreConnected ? "aa-locked" : undefined}>
          <DataTable
            tableId="add-app-branches"
            hideToolbar
            className="aa-branches"
            columns={branchTableColumns}
            data={paginatedBranches}
            rowKey="_id"
            loading={isLoadingBranches}
            isRtl={isArabic}
            tableMinWidth={760}
            maxBodyHeight={560}
            emptyState={t("No data available")}
            getRowClassName={() => (selectedStore && !selectedStoreConnected ? "is-locked" : "")}
            sort={{
              column: branchTableSortColumn,
              direction: branchSortDirection,
              onChange: ({ column, direction }) =>
                applyBranchTableSort(column, direction),
            }}
            pagination={{
              page: branchCurrentPage,
              pageSize: branchPageSize,
              total: sortedBranches.length,
              onPageChange: setBranchCurrentPage,
              onPageSizeChange: (size) => {
                setBranchPageSize(size);
                setBranchCurrentPage(1);
              },
              pageSizeOptions: [10, 20, 50],
              showLabel: t("show"),
              ofLabel: t("of", { defaultValue: "of" }),
              resultsLabel: t("results", { defaultValue: "results" }),
              isRtl: isArabic,
              previousLabel: t("previous"),
              nextLabel: t("next"),
            }}
          />
        </div>
      </section>
      <CreateStoreModal
        show={showCreateStoreModal}
        setShow={setShowCreateStoreModal}
        onStoreCreated={() => {
          fetchUserStores();
        }}
      />
    </div>
  );
}

export default AddApp;
